import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { ApiKeyDisplay } from './ApiKeyDisplay';
import { useApiKey } from '../hooks/useApiKey';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';

export const ApiKeyManagement = () => {
  const { apiKeys, displayKey, loading, error, createApiKey, deleteApiKey } = useApiKey();
  const [description, setDescription] = useState('');

  const handleCreate = async () => {
    await createApiKey(description.trim() || undefined);
    setDescription('');
  };

  if (loading) {
    return <LoadingSpinner text="Loading..." />;
  }

  return (
    <Card>
      <CardContent sx={{ pt: 4, pb: 4 }}>
        {error && (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        )}

        {displayKey && (
          <Stack direction="column" spacing={2} sx={{ mb: 2, alignItems: 'center', alignSelf: 'center' }}>
            <Alert severity="warning" sx={{ border: '1px solid', borderColor: 'info.warning' }}>
              Make sure to copy your API key now. You won't be able to see it again!
            </Alert>
            <ApiKeyDisplay apiKey={displayKey.key} />
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 3, alignItems: 'center', justifyContent: 'center' }}
        >
          <TextField
            label="Description"
            placeholder="What will this key be used for?"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 200 } }}
            sx={{ maxWidth: 360, width: '100%' }}
          />
          <Button variant="contained" onClick={handleCreate} disabled={loading}>
            Create API Key
          </Button>
        </Stack>

        {apiKeys.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Typography>You haven't created an API key yet</Typography>
          </Box>
        )}

        {apiKeys.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell>{apiKey.description ?? '—'}</TableCell>
                  <TableCell>
                    <code>{apiKey.shortKey}...</code>
                  </TableCell>
                  <TableCell>{new Date(apiKey.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => deleteApiKey(apiKey.id)}
                      disabled={loading}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default ApiKeyManagement;
